'use client';

import {
  type CmsHydratedImageSourceValue,
  resolveCmsImageRenderAsset,
} from '@/cms/image-source';
import {
  WsrvImage,
  type WsrvImageProps,
} from '@/components/ui/media/wsrv-image';
import { cn } from '@/lib/ui/utils';
import {
  WSRV_COMPACT_WIDTHS,
  WSRV_HERO_WIDTHS,
  WSRV_MATERIAL_WIDTHS,
  resolveWsrvResponsiveImageSource,
} from '@/lib/ui/wsrv';

export type CmsImageProps = Omit<WsrvImageProps, 'alt' | 'src'> & {
  alt?: string | null;
  src?: string | CmsHydratedImageSourceValue | null;
  variant?: CmsImageVariant;
};

export type CmsImageVariant = 'compact' | 'content' | 'hero' | 'full';

export const CMS_IMAGE_PRESETS: Record<
  CmsImageVariant,
  {
    sizes: string;
    widths: number[];
  }
> = {
  compact: {
    sizes: '(min-width: 768px) 320px, 60vw',
    widths: WSRV_COMPACT_WIDTHS,
  },
  content: {
    sizes: '(min-width: 1280px) 720px, (min-width: 768px) 56vw, 100vw',
    widths: WSRV_MATERIAL_WIDTHS,
  },
  hero: {
    sizes: '(min-width: 1280px) 760px, (min-width: 1024px) 54vw, 100vw',
    widths: WSRV_HERO_WIDTHS,
  },
  full: {
    sizes: '(min-width: 1280px) 1180px, 100vw',
    widths: WSRV_MATERIAL_WIDTHS,
  },
};

export type CmsImagePreloadOptions = {
  src?: string | null;
  variant?: CmsImageVariant;
  sizes?: string;
  widths?: number[];
  quality?: number;
  proxy?: boolean;
  fetchPriority?: CmsImageProps['fetchPriority'];
};

function resolveCmsImagePreloadSource({
  src,
  variant = 'content',
  sizes,
  widths,
  quality,
  proxy,
}: CmsImagePreloadOptions) {
  const normalizedSrc = src?.trim() ?? '';
  if (!normalizedSrc) {
    return null;
  }

  const preset = CMS_IMAGE_PRESETS[variant];
  const source = resolveWsrvResponsiveImageSource({
    src: normalizedSrc,
    widths: widths ?? preset.widths,
    quality,
    proxy,
  });

  return {
    ...source,
    sizes: source.srcSet ? (sizes ?? preset.sizes) : undefined,
  };
}

export function preloadCmsImage(
  options: CmsImagePreloadOptions
): Promise<void> {
  const source = resolveCmsImagePreloadSource(options);
  if (!source || typeof Image === 'undefined') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const image = new Image();
    if (options.fetchPriority) image.fetchPriority = options.fetchPriority;
    image.decoding = 'async';
    image.onload = () => {
      if (typeof image.decode !== 'function') {
        resolve();
        return;
      }

      void image.decode().then(resolve, resolve);
    };
    image.onerror = () => resolve();
    if (source.srcSet) {
      image.srcset = source.srcSet;
      image.sizes = source.sizes ?? '';
    }
    image.src = source.src;
  });
}

export function CmsImage({
  src,
  alt,
  className,
  id,
  loading = 'lazy',
  decoding = 'async',
  variant = 'content',
  sizes,
  style,
  title,
  width,
  height,
  widths,
  ...props
}: CmsImageProps) {
  const preset = CMS_IMAGE_PRESETS[variant];
  const asset = resolveCmsImageRenderAsset(src);
  const normalizedSrc = asset.src.trim();
  const normalizedAlt = alt?.trim() ?? '';

  if (!normalizedSrc) {
    return (
      <div
        id={id}
        className={cn('cms-image-placeholder', className)}
        style={style}
        title={title}
        data-cms-image-placeholder="true"
        role={normalizedAlt ? 'img' : undefined}
        aria-label={normalizedAlt || undefined}
        aria-hidden={normalizedAlt ? undefined : true}
      />
    );
  }

  return (
    <WsrvImage
      src={normalizedSrc}
      alt={normalizedAlt}
      className={className}
      id={id}
      loading={loading}
      decoding={decoding}
      sizes={sizes ?? preset.sizes}
      style={style}
      title={title}
      width={width ?? asset.dimensions?.width}
      height={height ?? asset.dimensions?.height}
      widths={widths ?? preset.widths}
      {...props}
    />
  );
}
